import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { validate as isUUID } from 'uuid';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, UncefactUnitSchema, unique } from '@vestigas/utils';

import { queryKeysDeliveryNote } from './queryKeys';
import { zodFilteredDeliveryNotesPayloadSchema } from './zodDeliveryNoteFiltersSchema';

const REPORT_DELIVERY_NOTE_FIELD_NAMES = [
  'article_name',
  'article_number',
  'billed_state',
  'confirmed_accounting_reference_id',
  'confirmed_accounting_reference_name',
  'confirmed_site_id',
  'confirmed_site_name',
  'dln_accept_state',
  'dln_date',
  'dln_nr',
  'loading_location',
  'process_state',
  'product_construction_component',
  'product_construction_plan',
  'recipient_id',
  'recipient_name',
  'supplier_assigned_site_name',
  'supplier_id',
  'supplier_name',
] as const;

const REPORT_CONFIG_FIELDS = [
  ...REPORT_DELIVERY_NOTE_FIELD_NAMES,
  'custom_field_global_level',
  'custom_field_item_level',
] as const;

const REPORT_SORT_FIELDS = [
  'amount',
  'article_name',
  'article_number',
  'billed_state',
  'dln_accept_state',
  'dln_date',
  'dln_nr',
  'process_state',
  'supplier_name',
] as const;

const REPORT_COLUMN_ORDER_FIELDS = [
  ...REPORT_DELIVERY_NOTE_FIELD_NAMES,
  'amount',
  'unit_type',
] as const;

export const zodDeliveryNoteReportSchema = zodFilteredDeliveryNotesPayloadSchema
  .omit({
    orderBy: true,
  })
  .extend({
    orderBy: z.preprocess(
      (value) => toSnakeCase(value),
      z.enum(REPORT_SORT_FIELDS).optional().prefault('amount'),
    ),
    reportConfig: z
      .array(
        z
          .object({
            name: z.preprocess(
              (value) => toSnakeCase(value),
              z.enum(REPORT_CONFIG_FIELDS),
            ),
            customFieldId: z.uuid().optional(),
          })
          .refine(
            (data) => {
              // if reportConfig.name is 'custom_field_global_level' or'custom_field_item_level',
              // we need to have a UUID, otherwise it should be undefined.
              if (
                data.name === 'custom_field_global_level' ||
                data.name === 'custom_field_item_level'
              ) {
                return isUUID(data.customFieldId);
              }

              return data.customFieldId === undefined;
            },
            {
              error:
                'You must provide a UUID as customFieldId for custom field config items.',
            },
          ),
      )
      .min(1)
      .transform((value) => unique(value)),
    reportOrder: z
      .array(
        z.preprocess(
          (value) => toSnakeCase(value),
          z.union([z.enum(REPORT_COLUMN_ORDER_FIELDS), z.uuid()]),
        ),
      )
      .min(1)
      .transform((value) => unique(value.map((item) => toSnakeCase(item)))),
    unitType: UncefactUnitSchema.nullable(),
  });

export type ReportOrderItem = (typeof REPORT_COLUMN_ORDER_FIELDS)[number];
export type SortableReportColumn = (typeof REPORT_SORT_FIELDS)[number];
export type ReportOrder = ReportOrderItem[];

export type DeliveryNoteReportRequest = z.input<
  typeof zodDeliveryNoteReportSchema
>;

export type DeliveryNoteReportValue = {
  article: string;
  total: number;
};

type DeliveryNoteReportResponse = {
  data: DeliveryNoteReportValue[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

/**
 * Fetches report data for delivery notes based on the provided parameters.
 *
 * @param payload - Parameters for filtering and configuring the report
 * @returns The report data values
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Analytics/operation/compute_delivery_note_analytics_report_analytics_report_post
 */
export const fetchDeliveryNoteReport = async (
  payload: DeliveryNoteReportRequest,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.GET_REPORT(), {
      json: payload,
      validate: {
        body: zodDeliveryNoteReportSchema,
      },
    })
    .json<Omit<DeliveryNoteReportResponse, 'totalCount'>>(); // totalCount is only faked

  return {
    data: response.values ?? [],
    hasNextPage: response.hasNextPage ?? false,
    paginatedCount: response.paginatedCount ?? 0,
    totalCount: 9999, // FIXME: totalCount is not returned by the API, but required for pagination in the datagrid
  } satisfies DeliveryNoteReportResponse;
};

/**
 * React Query based custom hook for fetching delivery note report data.
 *
 * @param payload - Parameters for filtering and configuring the report
 * @param options - Additional options for the useQuery hook
 * @returns The result of the useQuery hook containing the report data
 */
export const useQueryDeliveryNoteReport = (
  payload: DeliveryNoteReportRequest,
  options?: Omit<
    UseQueryOptions<DeliveryNoteReportValue[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryFn: async () => fetchDeliveryNoteReport(payload),
    queryKey: queryKeysDeliveryNote.getReport(payload),
    ...options,
  });
};
